/*
 * Product media carousel (mobile) - behaviour only.
 *
 * The markup, the images and the layout are rendered by Liquid in
 * snippets/product-media-carousel.liquid. What is left here is the part a
 * server cannot do: reacting to the visitor's scroll and playing videos when
 * they come into view.
 *
 * Ported from the Variant experience EXPA5C5848B. The original also played a
 * one-time "this swipes" hint 500ms after load, scrolling the track ~20%
 * towards the next slide and back. In this RTL store that read as the product
 * image jumping right while the page loaded, so it is deliberately left out.
 */
(function () {
  'use strict';

  var EXPERIMENT_ID = 'EXPA5C5848B';
  var VARIATION_ID = '1';

  function track(eventName, extra) {
    try {
      if (typeof Variant === 'undefined' || !Variant.publishCustomEvent) return;
      var payload = { experiment_id: EXPERIMENT_ID, variation_id: VARIATION_ID };
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) payload[key] = extra[key];
      }
      Variant.publishCustomEvent(eventName, payload);
    } catch (e) {
      /* never break the page for analytics */
    }
  }

  var PdpMediaCarousel = class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('[data-pdp-carousel-track]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-pdp-carousel-thumb]'));
      if (!this.track) return;

      this.navFired = {};
      this.scrollTimer = null;

      this.onThumbClick = this.onThumbClick.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.thumbs.forEach(
        function (thumb) {
          thumb.addEventListener('click', this.onThumbClick);
        }.bind(this)
      );
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.setupVideoAutoplay();

      track('influenced-revenue', { action: 'carousel_viewed' });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    get slides() {
      return this.track.children;
    }

    /**
     * Index of the slide whose centre is nearest the track's centre.
     * Measured with getBoundingClientRect because this is an RTL store and
     * scrollLeft-based arithmetic is not portable across engines there.
     */
    currentIndex() {
      var slides = this.slides;
      var trackRect = this.track.getBoundingClientRect();
      var trackCenter = trackRect.left + trackRect.width / 2;
      var active = 0;
      var closest = Infinity;

      for (var i = 0; i < slides.length; i++) {
        var rect = slides[i].getBoundingClientRect();
        var distance = Math.abs(rect.left + rect.width / 2 - trackCenter);
        if (distance < closest) {
          closest = distance;
          active = i;
        }
      }

      return active;
    }

    setActive(index) {
      this.thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    scrollToIndex(index) {
      var target = this.slides[index];
      if (!target) return;

      // Offset relative to the track's current position, which stays correct
      // in RTL where offsetLeft does not.
      var delta = target.getBoundingClientRect().left - this.track.getBoundingClientRect().left;
      this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'smooth' });
    }

    onThumbClick(event) {
      var thumb = event.currentTarget;
      var index = parseInt(thumb.getAttribute('data-pdp-carousel-thumb'), 10);
      var slide = this.slides[index];

      this.scrollToIndex(index);
      this.setActive(index);
      track('carousel_thumbnail_clicked', {
        slide_index: index,
        media_type: slide ? slide.getAttribute('data-media-type') : ''
      });
    }

    onScroll() {
      var active = this.currentIndex();
      this.setActive(active);

      if (this.scrollTimer) clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(
        function () {
          if (this.navFired[active]) return;
          this.navFired[active] = true;
          track('carousel_scrolled', { slide_index: active });
        }.bind(this),
        150
      );
    }

    /**
     * Play each video while it is on screen, pause it once it leaves. The
     * videos are muted, so browsers allow this without a user gesture.
     */
    setupVideoAutoplay() {
      var videos = Array.prototype.slice.call(this.querySelectorAll('.pdp-carousel__slide video'));
      if (!videos.length) return;

      var played = {};

      var play = function (video) {
        try {
          var promise = video.play();
          if (promise && typeof promise.catch === 'function') {
            promise.catch(function () {
              /* autoplay may be blocked; ignore */
            });
          }
        } catch (e) {
          /* ignore */
        }
      };

      if (!('IntersectionObserver' in window)) {
        videos.forEach(function (video, index) {
          play(video);
          played[index] = true;
          track('video_autoplayed', { slide_index: index });
        });
        return;
      }

      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var index = videos.indexOf(entry.target);

            if (!entry.isIntersecting) {
              try {
                entry.target.pause();
              } catch (e) {
                /* ignore */
              }
              return;
            }

            play(entry.target);
            if (!played[index]) {
              played[index] = true;
              track('video_autoplayed', { slide_index: index });
            }
          });
        },
        { threshold: 0.6 }
      );

      videos.forEach(
        function (video) {
          this.observer.observe(video);
        }.bind(this)
      );
    }

  };

  if (!window.customElements.get('pdp-media-carousel')) {
    window.customElements.define('pdp-media-carousel', PdpMediaCarousel);
  }
})();
